import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, XCircle } from '@phosphor-icons/react';
import { API } from '../lib/api';
import { useSEO } from '../lib/seo';
import { DashboardShell, Panel, StatCard, StatusPill, formatDate, inputCls } from '../components/dashboard/Bits';

const providerStatuses = ['founder_review', 'verified', 'paused', 'rejected'];
const rfqStatuses = ['new', 'founder_review', 'matching', 'matched', 'quotes_received', 'awarded', 'completed', 'cancelled'];

const FounderDashboard = () => {
  useSEO({ title: 'Founder Dashboard | Vox Meridian', description: '', path: '/admin/dashboard', robots: 'noindex, nofollow' });
  const [params, setParams] = useSearchParams();
  const [key, setKey] = useState(params.get('key') || localStorage.getItem('vm_admin_key') || '');
  const [data, setData] = useState(null);
  const [serviceData, setServiceData] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    owner_email: '',
    company_name: '',
    plan_status: 'pilot',
  });
  const [selectedProviders, setSelectedProviders] = useState({});
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!key) return;
    setLoading(true);
    try {
      const [{ data }, serviceRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { params: { key } }),
        axios.get(`${API}/admin/service-register`, { params: { key } }),
      ]);
      localStorage.setItem('vm_admin_key', key);
      setData(data);
      setServiceData(serviceRes.data);
      // One-click email approval redirects here with ?key=...#service-manager.
      // Once loaded, strip the key from the address bar and scroll to the panel.
      if (params.get('key')) {
        setParams({}, { replace: true });
        document.getElementById('service-manager')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid founder key.');
      localStorage.removeItem('vm_admin_key');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [key, params, setParams]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, success) => {
    try {
      await fn();
      toast.success(success);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed.');
    }
  };

  const providerStatus = (id, status) => act(
    () => axios.post(`${API}/admin/providers/${id}/status`, { verification_status: status }, { params: { key } }),
    'Provider status updated.'
  );
  const rfqStatus = (id, status) => act(
    () => axios.post(`${API}/admin/rfqs/${id}/status`, { status }, { params: { key } }),
    'RFQ status updated.'
  );
  const addMatch = (rfqId) => {
    const email = selectedProviders[rfqId];
    if (!email) return toast.error('Choose a provider first.');
    return act(
      () => axios.post(`${API}/admin/rfqs/${rfqId}/matches`, { provider_emails: [email] }, { params: { key } }),
      'Provider matched and emailed.'
    );
  };
  const interestStatus = (id, status) => act(
    () => axios.post(`${API}/admin/interests/${id}/status`, { status }, { params: { key } }),
    `Interest ${status}.`
  );
  const enableService = (e) => {
    e.preventDefault();
    return act(
      () => axios.post(`${API}/admin/service-register/enable`, serviceForm, { params: { key } }),
      'Vox Service Manager enabled.'
    );
  };
  const serviceStatus = (id, plan_status) => act(
    () => axios.post(`${API}/admin/service-register/${id}/status`, { plan_status }, { params: { key } }),
    'Vox Service Manager status updated.'
  );
  const profileStatus = (id, status) => act(
    () => axios.post(`${API}/admin/providers/${id}/profile-status`, { status }, { params: { key } }),
    `Directory profile ${status}.`
  );

  const pendingInterests = useMemo(() => (data?.interests || []).filter((i) => i.status === 'pending'), [data]);
  const pendingProfiles = useMemo(() => (data?.providers || []).filter((p) => p.profile_status === 'pending'), [data]);

  if (!key) {
    return (
      <DashboardShell eyebrow="Founder console" title={<>Founder key required.</>} sub="Use the private founder dashboard link.">
        <div className="mx-auto max-w-md border border-line bg-surface p-8" data-testid="founder-key-form">
          <label className="mono-label mb-2 block text-steel">Founder key</label>
          <input className={inputCls} value={key} onChange={(e) => setKey(e.target.value)} data-testid="founder-key-input" />
          <button type="button" onClick={load} data-testid="founder-key-submit" className="mt-5 w-full bg-volt px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink">Open console</button>
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell
      eyebrow="Founder console"
      title={<>Operate the marketplace.</>}
      sub="Verify providers, review RFQs, approve interest, add matches and track quote outcomes. Protected by the existing founder key."
    >
      {loading && !data ? (
        <p className="mono-label text-steel" data-testid="founder-loading">Loading console…</p>
      ) : !data ? (
        <div className="mx-auto max-w-md border border-line bg-surface p-8" data-testid="founder-key-form">
          <p className="font-display text-xl font-black text-parchment" data-testid="founder-invalid">Console not unlocked.</p>
          <p className="mt-2 text-sm text-steel">The saved key was rejected — it may have been rotated. Enter the current founder key below.</p>
          <label className="mono-label mb-2 mt-6 block text-steel">Founder key</label>
          <input className={inputCls} value={key} onChange={(e) => setKey(e.target.value)} data-testid="founder-key-input" />
          <button type="button" onClick={load} data-testid="founder-key-submit" className="mt-5 w-full bg-volt px-5 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink">Open console</button>
        </div>
      ) : (
        <>
          <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="founder-stats">
            <StatCard label="Providers" value={data.stats.providers} testId="founder-stat-providers" />
            <StatCard label="Need review" value={data.stats.providers_review} testId="founder-stat-review" />
            <StatCard label="RFQs" value={data.stats.rfqs} testId="founder-stat-rfqs" />
            <StatCard label="Quotes" value={data.stats.quotes} testId="founder-stat-quotes" />
            <StatCard label="Interests" value={data.stats.pending_interests} testId="founder-stat-interests" />
          </div>

          <div className="grid gap-6 xl:grid-cols-2">
            <Panel title="Provider applications" testId="founder-providers">
              <div className="space-y-4">
                {data.providers.map((p) => (
                  <div key={p.id} className="border border-line bg-ink p-4" data-testid={`founder-provider-${p.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-display text-base font-bold text-parchment">{p.company_name}</p>
                        <p className="mt-1 text-xs text-steel">{p.email} · {p.country}</p>
                      </div>
                      <StatusPill status={p.verification_status || 'verified'} />
                    </div>
                    <p className="mt-3 text-xs leading-relaxed text-steel">{(p.categories || []).join(' · ')}</p>
                    <p className="mt-2 text-xs text-steel">Markets: {(p.service_countries || [p.country]).join(', ')} · Modes: {(p.delivery_modes || ['on_site']).join(', ')}</p>
                    <div className="mt-4 flex gap-2">
                      <select className={inputCls} value={p.verification_status || 'verified'} onChange={(e) => providerStatus(p.id, e.target.value)} data-testid={`founder-provider-status-${p.id}`}>
                        {providerStatuses.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>

            <Panel title="RFQs" testId="founder-rfqs">
              <div className="space-y-4">
                {data.rfqs.map((r) => (
                  <div key={r.id} className="border border-line bg-ink p-4" data-testid={`founder-rfq-${r.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-display text-base font-bold text-parchment">{r.category}</p>
                        <p className="mt-1 text-xs text-steel">{r.company} · {r.email}</p>
                        <p className="mt-1 text-xs text-steel">{r.country_name} · {(r.delivery_mode || 'on_site').replace(/_/g, ' ')} · {formatDate(r.deadline)}</p>
                      </div>
                      <StatusPill status={r.status} />
                    </div>
                    <p className="mt-3 line-clamp-3 text-xs leading-relaxed text-steel">{r.scope}</p>
                    <p className="mt-3 text-xs text-steel">Matched: {(r.matched_providers || []).join(', ') || 'none'}</p>
                    <div className="mt-4 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
                      <select className={inputCls} value={r.status || 'new'} onChange={(e) => rfqStatus(r.id, e.target.value)} data-testid={`founder-rfq-status-${r.id}`}>
                        {rfqStatuses.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                      </select>
                      <select className={inputCls} value={selectedProviders[r.id] || ''} onChange={(e) => setSelectedProviders({ ...selectedProviders, [r.id]: e.target.value })} data-testid={`founder-match-select-${r.id}`}>
                        <option value="">Choose provider</option>
                        {data.providers.map((p) => <option key={p.id} value={p.email}>{p.company_name} — {p.email}</option>)}
                      </select>
                      <button type="button" onClick={() => addMatch(r.id)} data-testid={`founder-match-add-${r.id}`} className="bg-volt px-4 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink">Match</button>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          </div>

          <div className="mt-6 grid gap-6 xl:grid-cols-2">
            <Panel title="Provider interests" testId="founder-interests">
              {pendingInterests.length === 0 ? (
                <p className="text-sm text-steel" data-testid="founder-no-interests">No pending interests.</p>
              ) : (
                <div className="space-y-3">
                  {pendingInterests.map((i) => (
                    <div key={i.id} className="flex flex-wrap items-center justify-between gap-3 border border-line bg-ink p-4" data-testid={`founder-interest-${i.id}`}>
                      <div>
                        <p className="text-sm font-bold text-parchment">{i.provider_company}</p>
                        <p className="mt-1 text-xs text-steel">{i.provider_email} · RFQ {i.rfq_id}</p>
                      </div>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => interestStatus(i.id, 'approved')} data-testid={`interest-approve-${i.id}`} className="flex items-center gap-1.5 bg-volt px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-ink"><CheckCircle size={13} /> Approve</button>
                        <button type="button" onClick={() => interestStatus(i.id, 'declined')} data-testid={`interest-decline-${i.id}`} className="flex items-center gap-1.5 border border-line px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-steel"><XCircle size={13} /> Decline</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Quotes" testId="founder-quotes">
              <div className="space-y-3">
                {data.quotes.map((q) => (
                  <div key={q.id} className="border border-line bg-ink p-4" data-testid={`founder-quote-${q.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-bold text-parchment">{q.provider_company}</p>
                        <p className="mt-1 text-xs text-steel">{q.provider_email} · {q.currency} {q.amount}</p>
                      </div>
                      <StatusPill status={q.status} />
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          </div>
          <div className="mt-6">
            <Panel title="Directory profile reviews" testId="founder-profile-reviews">
              {pendingProfiles.length === 0 ? (
                <p className="text-sm text-steel" data-testid="founder-no-profile-reviews">No directory profiles waiting for review.</p>
              ) : (
                <div className="grid gap-4 lg:grid-cols-2">
                  {pendingProfiles.map((p) => (
                    <div key={p.id} className="border border-line bg-ink p-4" data-testid={`founder-profile-review-${p.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-bold text-parchment">{p.company_name}</p>
                        <StatusPill status="pending" />
                      </div>
                      <p className="mt-1 text-xs text-steel">{p.email}</p>
                      {p.profile?.tagline && <p className="mt-3 text-sm text-volt/90">{p.profile.tagline}</p>}
                      {p.profile?.about && <p className="mt-2 line-clamp-4 whitespace-pre-line text-xs leading-relaxed text-steel">{p.profile.about}</p>}
                      {(p.profile?.certifications || []).length > 0 && (
                        <p className="mt-2 text-xs text-steel">Certifications: {p.profile.certifications.join(' · ')}</p>
                      )}
                      {p.profile?.website && <p className="mt-1 text-xs text-steel">Website: {p.profile.website}</p>}
                      {p.profile?.photo?.storage_path && <p className="mt-1 text-xs text-volt">Logo uploaded</p>}
                      <div className="mt-4 flex gap-2">
                        <button type="button" onClick={() => profileStatus(p.id, 'approved')} data-testid={`profile-approve-${p.id}`} className="flex items-center gap-1.5 bg-volt px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-ink"><CheckCircle size={13} /> Approve</button>
                        <button type="button" onClick={() => profileStatus(p.id, 'rejected')} data-testid={`profile-reject-${p.id}`} className="flex items-center gap-1.5 border border-line px-3 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-steel"><XCircle size={13} /> Reject</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          </div>
          <div className="mt-6">
            <Panel id="service-manager" title="Vox Service Manager pilot" testId="founder-service-register">
              <form onSubmit={enableService} className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]" data-testid="founder-service-enable-form">
                <input required type="email" className={inputCls} placeholder="customer email" value={serviceForm.owner_email} onChange={(e) => setServiceForm({ ...serviceForm, owner_email: e.target.value })} data-testid="founder-service-email-input" />
                <input required className={inputCls} placeholder="company name" value={serviceForm.company_name} onChange={(e) => setServiceForm({ ...serviceForm, company_name: e.target.value })} data-testid="founder-service-company-input" />
                <button type="submit" className="bg-volt px-4 py-3 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink" data-testid="founder-service-enable-button">Enable</button>
              </form>
              <div className="mt-6 grid gap-6 lg:grid-cols-2">
                <div>
                  <p className="mono-label text-steel">Access requests</p>
                  <div className="mt-3 space-y-3">
                    {(serviceData?.requests || []).map((r) => (
                      <div key={r.id} className="border border-line bg-ink p-4" data-testid={`founder-service-request-${r.id}`}>
                        <div className="flex items-center justify-between gap-3"><p className="text-sm font-bold text-parchment">{r.company_name}</p><StatusPill status={r.status} /></div>
                        <p className="mt-1 text-xs text-steel">{r.owner_email}</p>
                        {r.notes && <p className="mt-2 text-xs text-steel">{r.notes}</p>}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="mono-label text-steel">Enabled companies</p>
                  <div className="mt-3 space-y-3">
                    {(serviceData?.customers || []).map((c) => (
                      <div key={c.id} className="border border-line bg-ink p-4" data-testid={`founder-service-customer-${c.id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div><p className="text-sm font-bold text-parchment">{c.company_name}</p><p className="mt-1 text-xs text-steel">{c.owner_email}</p></div>
                          <StatusPill status={c.plan_status} />
                        </div>
                        <select className={`${inputCls} mt-3`} value={c.plan_status} onChange={(e) => serviceStatus(c.id, e.target.value)} data-testid={`founder-service-status-${c.id}`}>
                          {['pilot', 'active', 'paused', 'cancelled'].map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.15em] text-steel" data-testid="founder-service-item-count">Tracked service items: {serviceData?.items?.length || 0}</p>
            </Panel>
          </div>
        </>
      )}
    </DashboardShell>
  );
};

export default FounderDashboard;
